import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Radio, Play, Pause, X, Users, Music, Volume2, Send, SkipForward, Plus, ListMusic, Link2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, mediaUrl, sessionId } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/PostCard";
import { toast } from "sonner";

const EMOJIS = ["❤️", "🔥", "🎉", "👏", "😍", "🙌"];

export default function Party() {
  const { partyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setIsPlaying } = usePlayer();
  const [party, setParty] = useState(null);
  const [joined, setJoined] = useState(false);
  const [ended, setEnded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [floats, setFloats] = useState([]);
  const [burst, setBurst] = useState(null);
  const [combo, setCombo] = useState(null);
  const comboCooldown = useRef(0);
  const [replay, setReplay] = useState(null);
  const [hostTracks, setHostTracks] = useState([]);

  const audioRef = useRef(null);
  const joinedRef = useRef(false);
  const isHostRef = useRef(false);
  const lastChatTs = useRef(null);
  const seenReactions = useRef(new Set());
  const chatEndRef = useRef(null);
  const sid = sessionId();

  const sync = useCallback((data) => {
    const a = audioRef.current;
    if (!a || !joinedRef.current || data.status === "ended") return;
    if (data.is_playing) {
      if (Math.abs(a.currentTime - data.position) > 2.5) a.currentTime = data.position;
      if (a.paused) a.play().catch(() => {});
    } else if (!a.paused) {
      a.pause();
    }
  }, []);

  const fetchParty = useCallback(async () => {
    try {
      const { data } = await api.get(`/parties/${partyId}`);
      setParty(data);
      isHostRef.current = data.is_host;
      if (data.status === "ended") setEnded(true);
      sync(data);
    } catch { setEnded(true); }
  }, [partyId, sync]);

  useEffect(() => {
    setIsPlaying(false);
    fetchParty();
    const poll = setInterval(fetchParty, 4000);
    return () => { clearInterval(poll); api.post(`/parties/${partyId}/leave`, { session_id: sid }).catch(() => {}); };
  }, [fetchParty]); // eslint-disable-line

  // Chat + reactions pulse
  useEffect(() => {
    const pulse = async () => {
      try {
        const url = `/parties/${partyId}/pulse${lastChatTs.current ? `?after_chat=${encodeURIComponent(lastChatTs.current)}` : ""}`;
        const { data } = await api.get(url);
        if (data.messages?.length) {
          setMessages((prev) => {
            const ids = new Set(prev.map((m) => m.id));
            const add = data.messages.filter((m) => !ids.has(m.id));
            return add.length ? [...prev, ...add] : prev;
          });
          lastChatTs.current = data.messages[data.messages.length - 1].created_at;
        }
        const fresh = (data.reactions || []).filter((r) => !seenReactions.current.has(r.id));
        fresh.forEach((r) => { seenReactions.current.add(r.id); spawnFloat(r.emoji); });
        if (fresh.length >= 3) triggerBurst(fresh);
        // Combo: same emoji streaming heavily in the recent window -> full-screen effect
        const counts = {};
        (data.reactions || []).forEach((r) => { counts[r.emoji] = (counts[r.emoji] || 0) + 1; });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        if (top && top[1] >= 5 && Date.now() - comboCooldown.current > 5000) {
          comboCooldown.current = Date.now();
          triggerCombo(top[0]);
        }
      } catch {}
    };
    const i = setInterval(pulse, 2000);
    pulse();
    return () => clearInterval(i);
  }, [partyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // heartbeat presence
  useEffect(() => {
    if (!joined) return;
    const beat = () => api.post(`/parties/${partyId}/join`, { session_id: sid }).then(({ data }) => { setParty(data); sync(data); }).catch(() => {});
    const i = setInterval(beat, 8000);
    return () => clearInterval(i);
  }, [joined, partyId, sid, sync]);

  const spawnFloat = (emoji) => {
    const key = Math.random().toString(36).slice(2);
    const left = 10 + Math.random() * 80;
    setFloats((f) => [...f, { key, emoji, left }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.key !== key)), 2600);
  };

  const triggerBurst = (fresh) => {
    const emojis = fresh.map((r) => r.emoji);
    for (let i = 0; i < 14; i++) spawnFloat(emojis[i % emojis.length]);
    setBurst(emojis[0] || "🎉");
    setTimeout(() => setBurst(null), 1300);
  };

  const triggerCombo = (emoji) => {
    setCombo({ emoji, cols: Array.from({ length: 22 }, () => ({ k: Math.random().toString(36).slice(2), left: Math.random() * 100, delay: Math.random() * 0.6 })) });
    setTimeout(() => setCombo(null), 2600);
  };

  const copyInvite = () => {
    const url = window.location.href;
    const done = () => toast.success("Invite link copied!");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => fallbackCopy(url, done));
    } else {
      fallbackCopy(url, done);
    }
  };

  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta); done();
    } catch { toast.error("Could not copy link"); }
  };

  const buyTicket = async () => {
    try { const { data } = await api.post(`/parties/${partyId}/ticket/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not buy ticket"); }
  };

  const joinAndPlay = async () => {
    const a = audioRef.current;
    try { await a.play(); } catch {}
    joinedRef.current = true;
    setJoined(true);
    try {
      const { data } = await api.post(`/parties/${partyId}/join`, { session_id: sid });
      setParty(data); sync(data);
      if (data.is_host) {
        const res = await api.get(`/artists/${data.host_id}`);
        setHostTracks(res.data.tracks || []);
      }
    } catch {}
  };

  const control = async (action) => {
    try { const { data } = await api.post(`/parties/${partyId}/control`, { action }); setParty(data); sync(data); }
    catch { toast.error("Only the host can control"); }
  };

  const addToQueue = async (trackId) => {
    if (!trackId) return;
    try { const { data } = await api.post(`/parties/${partyId}/queue`, { track_id: trackId }); setParty(data); toast.success("Added to queue"); } catch {}
  };

  const sendChat = async () => {
    if (!chatText.trim()) return;
    const text = chatText; setChatText("");
    try {
      const { data } = await api.post(`/parties/${partyId}/chat`, { text });
      setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      lastChatTs.current = data.created_at;
    } catch {}
  };

  const react = async (emoji) => { spawnFloat(emoji); try { await api.post(`/parties/${partyId}/react`, { emoji }); } catch {} };

  const endParty = async () => {
    try { await api.post(`/parties/${partyId}/end`); toast.success("Party ended"); audioRef.current?.pause(); setEnded(true); } catch {}
  };

  const onTrackEnded = () => { if (isHostRef.current) control("next"); };

  const shareRecap = async () => {
    try {
      const n = replay?.tracklist?.length || 1;
      await api.post("/posts", { text: `Relive the party — ${n} track${n > 1 ? "s" : ""} played 🎉`, party_id: partyId });
      toast.success("Recap shared to your feed!");
      navigate("/feed");
    } catch { toast.error("Could not share"); }
  };

  useEffect(() => {
    if (ended && !replay) api.get(`/parties/${partyId}/replay`).then(({ data }) => setReplay(data)).catch(() => {});
  }, [ended, replay, partyId]);

  if (!party && !ended) return <p className="text-zinc-500 text-center py-16 font-mono-nu">loading party…</p>;

  if (ended) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="party-ended">
        <div className="text-center">
          <Radio className="w-14 h-14 text-zinc-600 mx-auto" />
          <h1 className="mt-6 font-display font-extrabold text-2xl">This party has ended</h1>
          <p className="mt-2 text-zinc-400">{replay?.host?.artist_name ? `Hosted by ${replay.host.artist_name}` : "Here's the recap."}</p>
          <button onClick={() => navigate("/feed")} className="mt-6 px-6 py-3 rounded-full bg-emerald-500 text-black font-bold">Back to Feed</button>
          {replay && replay.host?.id === user.id && (
            <button onClick={shareRecap} data-testid="share-recap-btn"
              className="mt-6 ml-3 px-6 py-3 rounded-full border border-white/15 hover:border-emerald-500/60 font-bold transition-colors">Share recap to feed</button>
          )}
        </div>

        {replay && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="party-recap">
            <div className="nu-card p-5">
              <h3 className="flex items-center gap-2 font-bold mb-3"><ListMusic className="w-4 h-4 text-emerald-400" /> Tracks Played</h3>
              <div className="space-y-2">
                {replay.tracklist.map((q, i) => (
                  <div key={`${q.id}-${i}`} className="flex items-center gap-3">
                    <span className="w-5 text-center text-xs font-mono-nu text-zinc-500">{i + 1}</span>
                    {q.cover_url ? <img src={mediaUrl(q.cover_url)} alt="" className="w-9 h-9 rounded object-cover" />
                      : <span className="w-9 h-9 rounded bg-emerald-500/15 flex items-center justify-center"><Music className="w-4 h-4 text-emerald-400" /></span>}
                    <div className="min-w-0"><p className="text-sm font-semibold truncate">{q.title}</p><p className="text-xs text-zinc-500 truncate">{q.artist?.artist_name}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nu-card p-5">
              <h3 className="flex items-center gap-2 font-bold mb-3"><Users className="w-4 h-4 text-emerald-400" /> Chat Recap</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto hide-scrollbar">
                {replay.chat.length === 0 ? <p className="text-sm text-zinc-600">No messages this time.</p>
                  : replay.chat.map((m) => (
                    <div key={m.id} className="text-sm"><b className="text-zinc-300">{m.name}:</b> <span className="text-zinc-400">{m.text}</span></div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const t = party.track;
  const isHost = party.is_host;
  const queue = party.queue || [];
  const availableToQueue = hostTracks.filter((ht) => !queue.some((q) => q.id === ht.id));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6" data-testid="party-page">
      <audio ref={audioRef} src={mediaUrl(t.audio_url)} onEnded={onTrackEnded} />

      {/* Full-screen reaction combo */}
      <AnimatePresence>
        {combo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] pointer-events-none overflow-hidden" data-testid="reaction-combo">
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/10 via-transparent to-violet-500/10" />
            <motion.div initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: [0.3, 1.4, 1.1], opacity: [0, 1, 0.9] }} transition={{ duration: 0.6 }}
              className="absolute top-1/3 left-1/2 -translate-x-1/2 text-center">
              <div className="text-8xl">{combo.emoji}</div>
              <div className="mt-2 font-display font-black text-2xl text-gradient">COMBO!</div>
            </motion.div>
            {combo.cols.map((c) => (
              <motion.span key={c.k} initial={{ y: "110vh", opacity: 0 }} animate={{ y: "-20vh", opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.4, delay: c.delay, ease: "easeOut" }}
                className="absolute text-4xl" style={{ left: `${c.left}%` }}>{combo.emoji}</motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Player + queue */}
        <div className="lg:col-span-7 space-y-6">
          <div className="beam-border rounded-3xl p-6 sm:p-8 relative overflow-hidden noise text-center">
            {/* floating reactions */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="reactions-layer">
              <AnimatePresence>
                {floats.map((f) => (
                  <motion.span key={f.key} initial={{ y: 0, opacity: 0, scale: 0.6 }} animate={{ y: -260, opacity: [0, 1, 1, 0], scale: 1.2 }}
                    exit={{ opacity: 0 }} transition={{ duration: 2.5, ease: "easeOut" }}
                    className="absolute bottom-8 text-3xl" style={{ left: `${f.left}%` }}>{f.emoji}</motion.span>
                ))}
              </AnimatePresence>
              <AnimatePresence>
                {burst && (
                  <motion.div key="burst" initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: [0.2, 1.6, 1.3], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.3 }} data-testid="emoji-burst"
                    className="absolute inset-0 flex items-center justify-center text-7xl sm:text-8xl">{burst}</motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 font-mono-nu text-xs uppercase tracking-widest">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" /></span>
                Live Listen Party
              </span>
            </div>

            <div className="relative w-40 h-40 sm:w-52 sm:h-52 mx-auto rounded-2xl overflow-hidden glow-emerald">
              {t.cover_url ? <img src={mediaUrl(t.cover_url)} alt="" className={`w-full h-full object-cover ${party.is_playing ? "animate-pulse" : ""}`} />
                : <div className="w-full h-full bg-gradient-to-br from-emerald-500/30 to-violet-500/20 flex items-center justify-center"><Music className="w-16 h-16 text-emerald-400" /></div>}
            </div>

            <h1 className="mt-5 font-display font-black text-2xl tracking-tight" data-testid="party-track-title">{t.title}</h1>
            <div className="mt-2 flex items-center justify-center gap-2 text-zinc-400 text-sm">
              <Avatar artist={party.host} size={22} /> Hosted by <b className="text-white">{party.host?.artist_name}</b>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 font-mono-nu text-sm text-emerald-400" data-testid="party-listeners">
              <Users className="w-4 h-4" /> {party.listeners} listening now
            </div>
            <div className="mt-3 flex justify-center">
              <button onClick={copyInvite} data-testid="party-invite-btn"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-white/15 hover:border-emerald-500/60 text-xs font-semibold text-zinc-300 transition-colors">
                <Link2 className="w-3.5 h-3.5" /> Copy invite link
              </button>
            </div>

            {!joined ? (
              party.ticket_price > 0 && !party.has_ticket ? (
                <button onClick={buyTicket} data-testid="party-buy-ticket"
                  className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-bold text-lg transition-colors">
                  <Play className="w-5 h-5" /> Buy ticket · ${party.ticket_price.toFixed(2)}
                </button>
              ) : (
                <button onClick={joinAndPlay} data-testid="party-join-play"
                  className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-lg transition-colors">
                  <Play className="w-5 h-5" /> Join & Play Together
                </button>
              )
            ) : (
              <>
                <div className="mt-6 flex items-center justify-center gap-2">
                  <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-sm font-mono-nu text-zinc-300" data-testid="party-sync-status">
                    <Volume2 className="w-4 h-4 text-emerald-400" /> {party.is_playing ? "Synced · Playing" : "Paused by host"}
                  </span>
                </div>
                {/* reactions */}
                <div className="mt-5 flex items-center justify-center gap-2" data-testid="reaction-buttons">
                  {EMOJIS.map((e) => (
                    <button key={e} onClick={() => react(e)} data-testid={`react-${e}`}
                      className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 text-xl transition-transform hover:scale-110">{e}</button>
                  ))}
                </div>
              </>
            )}

            {isHost && joined && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3 border-t border-white/10 pt-6">
                <span className="text-xs font-mono-nu uppercase tracking-widest text-zinc-500 w-full">Host controls</span>
                {party.is_playing ? (
                  <button onClick={() => control("pause")} data-testid="party-host-pause" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold"><Pause className="w-4 h-4" /> Pause</button>
                ) : (
                  <button onClick={() => control("play")} data-testid="party-host-play" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 text-black text-sm font-bold"><Play className="w-4 h-4" /> Play</button>
                )}
                <button onClick={() => control("next")} disabled={party.queue_index + 1 >= queue.length} data-testid="party-host-skip"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold disabled:opacity-40"><SkipForward className="w-4 h-4" /> Skip</button>
                <button onClick={() => control("restart")} data-testid="party-host-restart" className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold">Restart</button>
                <button onClick={endParty} data-testid="party-end" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/25 text-sm font-semibold"><X className="w-4 h-4" /> End</button>
              </div>
            )}
          </div>

          {/* Queue */}
          <div className="nu-card p-5" data-testid="party-queue">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 font-bold"><ListMusic className="w-4 h-4 text-emerald-400" /> Queue</h3>
              {isHost && availableToQueue.length > 0 && (
                <select onChange={(e) => { addToQueue(e.target.value); e.target.value = ""; }} defaultValue="" data-testid="queue-add-select"
                  className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-zinc-300 outline-none">
                  <option value="">+ Add track…</option>
                  {availableToQueue.map((ht) => <option key={ht.id} value={ht.id}>{ht.title}</option>)}
                </select>
              )}
            </div>
            <div className="space-y-1">
              {queue.map((q, i) => (
                <div key={`${q.id}-${i}`} className={`flex items-center gap-3 p-2 rounded-lg ${i === party.queue_index ? "bg-emerald-500/10" : ""}`} data-testid="queue-item">
                  <span className="w-5 text-center text-xs font-mono-nu text-zinc-500">{i === party.queue_index ? "▶" : i + 1}</span>
                  {q.cover_url ? <img src={mediaUrl(q.cover_url)} alt="" className="w-9 h-9 rounded object-cover" />
                    : <span className="w-9 h-9 rounded bg-emerald-500/15 flex items-center justify-center"><Music className="w-4 h-4 text-emerald-400" /></span>}
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold truncate ${i === party.queue_index ? "text-emerald-400" : ""}`}>{q.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{q.artist?.artist_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chat sidebar */}
        <div className="lg:col-span-5">
          <div className="nu-card flex flex-col h-[70vh]" data-testid="party-chat">
            <div className="p-4 border-b border-white/10 font-bold text-sm flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Party Chat</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar" data-testid="chat-messages">
              {messages.length === 0 && <p className="text-center text-zinc-600 text-sm py-8">Say hi to the party 👋</p>}
              {messages.map((m) => (
                <div key={m.id} className="flex items-start gap-2">
                  {m.avatar ? <img src={mediaUrl(m.avatar)} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                    : <span className="w-7 h-7 rounded-full bg-violet-500/25 flex items-center justify-center text-violet-200 text-[10px] font-bold shrink-0">{m.name?.[0]?.toUpperCase() || "A"}</span>}
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-zinc-300">{m.name}</p>
                    <p className="text-sm text-zinc-200 break-words">{m.text}</p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Send a message…" data-testid="chat-input"
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500/50" />
              <button onClick={sendChat} data-testid="chat-send" className="w-10 h-10 rounded-full bg-emerald-500 text-black flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
